import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { entityRows } from "../lib/api";
import { t } from "../i18n";

// WorkCenter — the legacy GL work-queue panel: pagelets for My Work, approvals,
// errors, quick links and a period summary. Queues are derived from
// ps_journal_header (status D=draft/edit, P=posted/pending approval).

interface JRow { journal_id: string; journal_date: string; jrnl_hdr_status: string; description: string }

function Pagelet({ title, count, children }: { title: string; count?: number; children: ReactNode }) {
  return (
    <section className="pagelet">
      <div className="pagelet__head">
        <span className="pagelet__title">{t(title)}</span>
        {count !== undefined && <span className="pagelet__count">{count}</span>}
      </div>
      {children}
    </section>
  );
}

function Queue({ rows, empty }: { rows: JRow[]; empty: string }) {
  if (!rows.length) return <div className="pagelet__empty">{t(empty)}</div>;
  return (
    <ul className="pagelet__list">
      {rows.slice(0, 6).map((j) => (
        <li key={j.journal_id + j.journal_date}>
          <Link to="/gl?t=journals" className="pl-item">{j.journal_id} · {j.description || j.journal_date}</Link>
        </li>
      ))}
    </ul>
  );
}

const QUICK_LINKS: [string, string][] = [
  ["+ New Journal", "/gl?t=entry"],
  ["Journals Inbox", "/gl?t=journals"],
  ["Ledger Inquiry", "/gl?t=balances"],
  ["Chart of Accounts", "/entities"],
  ["Business Units", "/soon"],
  ["Accounting Periods", "/soon"],
  ["Year-End Close", "/gl?t=yearend"],
  ["Import Journals (Excel)", "/soon"],
  ["Trial Balance", "/gl?t=balances"],
  ["Balance Sheet", "/gl?t=reports"],
  ["Income Statement", "/gl?t=reports"],
  ["User Manual", "/soon"],
];

export function WorkCenterPage() {
  const [rows, setRows] = useState<JRow[]>([]);
  useEffect(() => { entityRows<JRow>("ps_journal_header").then(setRows).catch(() => setRows([])); }, []);

  const drafts = rows.filter((j) => ["D", "E"].includes(j.jrnl_hdr_status));
  const pending = rows.filter((j) => j.jrnl_hdr_status === "P");
  const errors = rows.filter((j) => j.jrnl_hdr_status === "X");
  const posted = rows.filter((j) => ["P", "A"].includes(j.jrnl_hdr_status)).length;

  return (
    <div className="page">
      <div className="crumbs"><Link to="/">{t("nav.home")}</Link> › {t("WorkCenter")}</div>
      <div className="page-head"><h2>{t("WorkCenter")} · {t("nav.gl")}</h2></div>
      <p className="exec-sub">{t("Consolidated panel · work queues, quick links and period summary.")}</p>

      <div className="pagelet-grid">
        <Pagelet title="My Work" count={drafts.length}>
          <Queue rows={drafts} empty="No pending journals." />
        </Pagelet>
        <Pagelet title="Budget Exceptions" count={0}>
          <div className="pagelet__empty">{t("No budget exceptions.")}</div>
        </Pagelet>
        <Pagelet title="Pending Approval" count={pending.length}>
          <Queue rows={pending} empty="No journals pending your approval." />
        </Pagelet>
        <Pagelet title="ChartField Requests" count={0}>
          <div className="pagelet__empty">{t("No pending changes.")}</div>
        </Pagelet>
        <Pagelet title="Pending Reconciliations" count={0}>
          <div className="pagelet__empty">{t("No accounts assigned.")}</div>
        </Pagelet>
        <Pagelet title="Errors (Edit)" count={errors.length}>
          <Queue rows={errors} empty="No errors." />
        </Pagelet>

        <Pagelet title="Quick Links">
          <ul className="pagelet__list links">
            {QUICK_LINKS.map(([label, to]) => <li key={label}><Link to={to}>{t(label)}</Link></li>)}
          </ul>
        </Pagelet>
        <Pagelet title="Period Summary">
          <dl className="pagelet__kpis">
            <div><dt>{t("Total journals")}</dt><dd>{rows.length}</dd></div>
            <div><dt>{t("Posted")}</dt><dd>{posted}</dd></div>
            <div><dt>{t("Draft / In Edit")}</dt><dd>{drafts.length}</dd></div>
            <div><dt>{t("With errors")}</dt><dd>{errors.length}</dd></div>
          </dl>
        </Pagelet>
      </div>
    </div>
  );
}
